import { useMutation, useQueryClient } from '@tanstack/react-query';
import { strapiUpdateManagedUser } from '../../api/strapiUsers';
import { queryKeys } from '../queryKeys';

/** Bulk-deactivate managed users (`blocked: true` so they cannot log in). */
export function useBulkDeactivateManagedUsersMutation(jwt: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ userIds }: { userIds: string[] }) => {
      if (!jwt) throw new Error('Not signed in');
      if (userIds.length === 0) throw new Error('Select at least one user.');

      const failures: { id: string; message: string }[] = [];
      for (const id of userIds) {
        try {
          await strapiUpdateManagedUser(jwt, id, { blocked: true });
        } catch (err) {
          failures.push({
            id,
            message: err instanceof Error ? err.message : 'Unable to deactivate user.',
          });
        }
      }

      if (failures.length > 0) {
        await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
        const preview = failures
          .slice(0, 3)
          .map((f) => `${f.id}: ${f.message}`)
          .join('; ');
        const more = failures.length > 3 ? ` (+${failures.length - 3} more)` : '';
        throw new Error(
          `Deactivated ${userIds.length - failures.length} of ${userIds.length} users. Failed: ${preview}${more}`,
        );
      }

      return { deactivatedCount: userIds.length };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
    },
  });
}
